/**
 * Карусель кейсов на главной (#cases).
 *
 * Механика — Swiper (подключается в index.blade.php из resources/landing/public/vendor/swiper).
 * Здесь только настройка под наш макет.
 *
 * Свою реализацию писали и переписывали несколько раз, к ней возвращаться не надо. Ни один
 * заход не выдержал реального ввода: через `scrollLeft` браузер вмешивается в позицию своими
 * средствами (инерция, scroll-snap, отмена жестов через cancelable=false) — были рывки, откаты
 * и двойные шаги; через собственный трансформ пропали конфликты с браузером, но остались
 * шероховатости на тачпаде и касаниях, которые Swiper давно решил и покрыл тестами на реальных
 * устройствах.
 */

/**
 * Ниже этой ширины листается только вперёд (карточки выезжают справа налево) — это про
 * телефоны и планшеты, а не про кнопки: кнопок нет уже ниже 1440 (media в _cases.scss),
 * но на ноутбуке 1200px листать назад жестом можно.
 */
const TOUCH_QUERY = '(max-width: 991.98px)';

/** Максимум карточек на экране по макету. Из него считается минимальная длина ленты. */
const MAX_CARDS_ON_SCREEN = 3;

/**
 * Сколько слайдов нужно Swiper, чтобы крутить ленту по кругу: на ОДИН больше, чем видно.
 * Переставляя слайд из начала в конец, он опирается на то, что за активным есть ещё хотя бы
 * один — иначе вперёд идти некуда, кроме служебной позиции «хвост ленты у правого края».
 */
const MIN_SLIDES_FOR_LOOP = MAX_CARDS_ON_SCREEN + 1;

/** Сколько после горизонтального жеста считать колесо его хвостом. */
const WHEEL_TAIL_MS = 450;

/** Вертикальный рывок больше этого — уже настоящая прокрутка страницы, а не хвост. */
const WHEEL_TAIL_MAX_DELTA = 40;

/** Открывает ленту: до этого она спрятана, чтобы не показывать слипшиеся карточки. */
function reveal(viewport) {
  viewport.classList.add('cases__viewport--ready');
}

export function initCasesCarousel() {
  const containers = document.querySelectorAll('[data-cases-carousel]');

  if (!containers.length) {
    return;
  }

  if (typeof window.Swiper !== 'function') {
    // Библиотека не доехала — показываем карточки как есть: они просто стоят в ряд и не
    // листаются. Оставить ленту спрятанной было бы хуже, чем показать её без карусели.
    containers.forEach(reveal);

    return;
  }

  containers.forEach((viewport) => {
    try {
      setupCarousel(viewport);
    } finally {
      // finally, а не после setupCarousel: споткнись инициализация на чём угодно, лента
      // всё равно должна стать видимой.
      requestAnimationFrame(() => reveal(viewport));
    }
  });
}

function setupCarousel(viewport) {
  const track = viewport.querySelector('[data-cases-track]');

  if (!track) {
    return;
  }

  const prevEl = viewport.querySelector('[data-cases-prev]');
  const nextEl = viewport.querySelector('[data-cases-next]');
  /* Снимок до инициализации: дальше Swiper переставляет слайды местами, и «исходный порядок»
     из DOM уже не прочитать, а копии набора надо добавлять именно в нём. Из него же берётся
     число кейсов — второй раз опрашивать DOM незачем. */
  const cards = [...track.querySelector('.swiper-wrapper').children];

  const swiper = new window.Swiper(track, {
    // Ширину слайда задаёт CSS карточки (411px, ниже 768px — 320px, ниже 390px — вся лента),
    // поэтому 'auto'.
    slidesPerView: 'auto',
    spaceBetween: 20,
    /* Бесконечная лента: слайды по кругу переставляет сам Swiper, копий он не создаёт. На
       коротком наборе переставлять ему нечего — тогда ленту достраивает копиями
       keepStripLongEnoughToLoop.

       Кейс может быть один — тогда крутить нечего вовсе. Единственному слайду loop не помогает,
       а мешает: Swiper пишет в консоль «The number of slides is not enough for loop mode» и всё
       равно принимается переставлять слайд по своей loop-арифметике. Такую карточку показываем
       как есть — целиком и по центру ленты (центрирует centerInsufficientSlides). */
    loop: cards.length > 1,
    // Скорость переключения. Одна и та же для кнопок, колеса и свайпа.
    speed: 600,
    // Все слайды поместились — Swiper выключается сам и прячет кнопки.
    watchOverflow: true,
    // ...и в этом случае центрирует ленту. CSS-ом это задавать нельзя: центрирование сместило
    // бы её и тогда, когда карточки не влезли, спрятав начало за левым краем.
    centerInsufficientSlides: true,
    navigation: {
      prevEl,
      nextEl,
      disabledClass: 'cases__arrow--disabled',
      lockClass: 'cases__arrow--locked',
    },
    pagination: {
      el: viewport.querySelector('[data-cases-pagination]'),
      clickable: true,
      // Все классы свои: у Swiper на его собственные повешено позиционирование (абсолютное,
      // внутри .swiper), а наши точки стоят обычным блоком под лентой.
      bulletClass: 'cases__dot',
      bulletActiveClass: 'cases__dot--active',
      modifierClass: 'cases__dots--',
      horizontalClass: 'cases__dots--horizontal',
      clickableClass: 'cases__dots--clickable',
      lockClass: 'cases__dots--locked',
    },
    mousewheel: {
      // Реагируем только на горизонтальное намерение: вертикальное колесо должно скроллить
      // страницу, а не листать карусель. Ниже 1440, где кнопок нет, лента всё равно
      // листается — горизонтальным жестом тачпада, пальцем и перетаскиванием мышью
      // (allowTouchMove у Swiper включён по умолчанию).
      forceToAxis: true,
      // Мелкую дрожь тачпада не считаем жестом.
      thresholdDelta: 6,
    },
    a11y: {
      // Подписи берём из разметки: они локализованы, а свои Swiper поставил бы по-английски.
      prevSlideMessage: prevEl?.getAttribute('aria-label') || 'Previous slide',
      nextSlideMessage: nextEl?.getAttribute('aria-label') || 'Next slide',
    },
  });

  /**
   * На мобиле и планшете листаем только вперёд — карточки выезжают справа налево.
   * Ставится не через breakpoints: allowSlidePrev там не поддерживается, зато меняется на лету.
   */
  const touchOnly = window.matchMedia(TOUCH_QUERY);

  const applyDirectionLock = () => {
    swiper.allowSlidePrev = !touchOnly.matches;
  };

  applyDirectionLock();

  // Порт события отличается у старых браузеров; addEventListener есть не везде.
  if (typeof touchOnly.addEventListener === 'function') {
    touchOnly.addEventListener('change', applyDirectionLock);
  } else if (typeof touchOnly.addListener === 'function') {
    touchOnly.addListener(applyDirectionLock);
  }

  keepStripLongEnoughToLoop(swiper, cards);
  keepOneDotPerCase(swiper, cards.length);
  stopWhenAllCardsFit(swiper, viewport);
  keepStripAlignedToSlideStart(swiper);
  holdPageDuringWheelGesture(track, swiper);
  suppressHoverAfterClick(track);
}

/**
 * Достраивает ленту копиями набора, когда карточек меньше, чем нужно для прокрутки по кругу.
 *
 * Swiper 11 крутит ленту не копиями, а перестановкой самих слайдов, и для этого ему нужно
 * на один слайд больше, чем видно на экране. Ровно три кейса на 1200px дают три слайда при
 * трёх видимых началах — переставлять нечего, и вперёд Swiper уходит только на служебную
 * позицию «хвост ленты у правого края». Дальше её отменяет `keepStripAlignedToSlideStart`,
 * и со стороны это выглядит как дёрганье: лента шагнула и прыжком вернулась.
 *
 * Поэтому копии набора добавляем сами — но ТОЛЬКО пока настоящие карточки не влезают в полосу.
 * Влезли — копии убираем: иначе лента перестанет считаться поместившейся, Swiper не запрёт её
 * (`watchOverflow`) и не поставит по центру (`centerInsufficientSlides`), а два-три кейса
 * на широком экране снова начнут листаться впустую.
 *
 * Набор повторяется ЦЕЛИКОМ, а не добирается по одной карточке: тогда позиции ленты кратны
 * числу кейсов, и точка под лентой остаётся одна на кейс (см. keepOneDotPerCase).
 */
function keepStripLongEnoughToLoop(swiper, cards) {
  const wrapper = swiper.slidesEl;
  const gap = swiper.params.spaceBetween;

  let copies = 0;
  let rebuilding = false;

  /** Ширина ленты из ОДНОГО набора — той, что задумана в макете, без наших копий. */
  const measureRealStrip = () => cards.length * cards[0].offsetWidth + (cards.length - 1) * gap;

  const rebuild = (wanted) => {
    // Порядок обязателен: loopDestroy возвращает слайды в исходный порядок и снимает
    // с них индексы, иначе копии уехали бы в перетасованную ленту, а loopCreate раздал бы
    // индексы по перепутанному DOM.
    swiper.loopDestroy();

    wrapper.querySelectorAll('[data-cases-copy]').forEach((copy) => copy.remove());

    for (let pass = 0; pass < wanted; pass += 1) {
      cards.forEach((card) => {
        const copy = card.cloneNode(true);

        copy.setAttribute('data-cases-copy', '');
        // Классы состояния и размеры Swiper проставляет сам; принесённые клоном сбили бы
        // расчёт до первого пересчёта.
        copy.className = card.className.split(' ').filter((name) => !name.startsWith('swiper-slide-')).join(' ');
        copy.removeAttribute('style');

        wrapper.append(copy);
      });
    }

    swiper.recalcSlides();
    swiper.loopCreate();
    swiper.update();

    copies = wanted;
  };

  const apply = () => {
    // update() внутри rebuild сам присылает события ширины — без флага получилась бы рекурсия.
    if (rebuilding) {
      return;
    }

    const overflows = measureRealStrip() > swiper.size + 1;
    const wanted = overflows ? Math.max(Math.ceil(MIN_SLIDES_FOR_LOOP / cards.length) - 1, 0) : 0;

    if (wanted === copies) {
      return;
    }

    rebuilding = true;

    try {
      rebuild(wanted);
    } finally {
      rebuilding = false;
    }
  };

  swiper.on('resize', apply);
  apply();
}

/**
 * Одна точка на кейс, а не на позицию ленты.
 *
 * Своих точек Swiper рисует по числу слайдов, а с копиями набора (см. keepStripLongEnoughToLoop)
 * слайдов кратно больше кейсов — три кейса дали бы шесть точек. Лишние прячем, а активную
 * ставим по кейсу: позиции ленты кратны числу кейсов, поэтому кейс ведущей карточки — это
 * остаток `realIndex` от деления на их число.
 *
 * Нажатие на точку при этом работает как есть: Swiper ведёт к слайду с её номером, а первые
 * `cards.length` слайдов — это и есть кейсы, по одному разу.
 */
function keepOneDotPerCase(swiper, caseCount) {
  const trim = () => {
    const dots = swiper.pagination?.bullets;

    if (!dots?.length) {
      return;
    }

    dots.forEach((dot, index) => {
      dot.hidden = index >= caseCount;
      dot.classList.remove('cases__dot--active');
    });

    dots[swiper.realIndex % caseCount]?.classList.add('cases__dot--active');
  };

  // paginationRender — точки перерисованы (изменилось число слайдов), paginationUpdate —
  // Swiper только что сам расставил активную.
  swiper.on('paginationRender', trim);
  swiper.on('paginationUpdate', trim);
  trim();
}

/**
 * Все карточки поместились — карусель не реагирует ни на что: ни жест, ни колесо, ни точки.
 *
 * Своего `watchOverflow` тут не хватает: он только запрещает переходы (`allowSlideNext/Prev`),
 * а обработку ввода оставляет включённой. Позиция ленты действительно не меняется, но
 * `slidePrev()` успевает вызвать `loopFix()` ДО проверки запрета — и тот переставляет слайды
 * в DOM. Лента стоит на месте, а карточки на каждый жест прокручиваются по кругу
 * (на двух кейсах порядок 0,1 становится 1,0). Со стороны это и выглядит как «карусель
 * листается, хотя листать нечего».
 *
 * Поэтому на запертой ленте Swiper выключаем целиком (`disable`): выключенный он выходит
 * из `slideNext`/`slidePrev` первой строкой, до `loopFix`, и не слушает колесо. Разрешение
 * ширины возвращает всё назад — Swiper сам считает «поместились или нет» на каждом
 * пересчёте и присылает `lock`/`unlock`.
 */
function stopWhenAllCardsFit(swiper, viewport) {
  const dots = viewport.querySelector('[data-cases-pagination]');

  const apply = () => {
    const allFit = swiper.isLocked;

    if (allFit) {
      swiper.disable();
    } else {
      swiper.enable();
    }

    // Точки прячем сами: свой lockClass Swiper ставит только включённой ленте, то есть
    // ровно не в этом случае. Класс тот же, что у него в настройках, — правило одно (CSS).
    dots?.classList.toggle('cases__dots--locked', allFit);
  };

  swiper.on('lock', apply);
  swiper.on('unlock', apply);
  // Первый раз Swiper посчитал это внутри init, когда нас ещё не подписали.
  apply();
}

/**
 * Держит ленту началом слайда у левого края — обрезка всегда справа, при любой ширине.
 *
 * Swiper в своём `snapGrid` выбрасывает позиции, уехавшие за конец ленты, и дописывает вместо
 * них последнюю — «прижать хвост ленты к ПРАВОМУ краю» (`virtualSize - size`). Лента у loop-режима
 * конечная: копий слайдов Swiper не создаёт, он переставляет сами слайды, и делает это, только
 * когда активная карточка подошла к концу на `loopedSlides` (у наших настроек это ОДИН слайд,
 * а на экране их три). Значит до перестановки активная успевает встать на ту самую последнюю
 * позицию — и выравнивание переворачивается: карточки прижаты к правому краю, обрезается левая.
 *
 * Пока ширина не меняется, разница почти не видна (сдвиг в один зазор), но при изменении ширины
 * позиция пересчитывается под новую, и обрезка слева вырастает до сотен пикселей — это и есть
 * баг, из-за которого сужение окна переворачивало карусель.
 *
 * Поэтому после каждой остановки ленты проверяем, стоит ли она на начале какого-нибудь слайда
 * (`slidesGrid` — начала слайдов, в отличие от `snapGrid` их никто не подменяет). Не стоит —
 * сначала просим Swiper переставить слайды (`loopFix`), и только если лента слишком коротка
 * даже для этого (двум карточкам на узком экране крутиться некуда), сдвигаем ленту ВПРАВО до
 * начала ближайшего слайда: вправо, а не влево, потому что влево — это снова обрезать левую
 * карточку.
 */
function keepStripAlignedToSlideStart(swiper) {
  const isAlignedToSlide = () => swiper.slidesGrid.some(
    (offset) => Math.abs(offset + swiper.translate) < 1,
  );

  const align = () => {
    // Заперта — все карточки поместились, Swiper держит их по центру, и обрезать нечего.
    if (swiper.destroyed || swiper.isLocked || isAlignedToSlide()) {
      return;
    }

    swiper.loopFix();

    if (isAlignedToSlide()) {
      return;
    }

    const at = -swiper.translate;
    let offset = 0;

    swiper.slidesGrid.forEach((candidate) => {
      if (candidate <= at + 1) {
        offset = candidate;
      }
    });

    swiper.setTransition(0);
    swiper.setTranslate(-offset);
    swiper.updateProgress(-offset);
    swiper.updateActiveIndex();
    swiper.updateSlidesClasses();
  };

  /**
   * Через два кадра: обработчик ширины внутри Swiper доводит позицию своим requestAnimationFrame,
   * и правку надо ставить ПОСЛЕ него, иначе он тут же перебьёт её обратно.
   */
  const alignLater = () => requestAnimationFrame(() => requestAnimationFrame(align));

  // Три события, и все нужны: ширина (сам баг), доезд переключения (кнопки, точки, колесо)
  // и отпущенный палец/мышь после перетаскивания.
  swiper.on('resize', alignLater);
  swiper.on('transitionEnd', alignLater);
  swiper.on('touchEnd', alignLater);
}

/**
 * Не даёт странице подъехать вниз на хвосте горизонтального жеста колеса.
 *
 * Swiper с `forceToAxis` обрабатывает событие, только если `|deltaX| > |deltaY|`, иначе выходит
 * БЕЗ `preventDefault` — и такое событие достаётся странице. У горизонтального жеста на тачпаде
 * хвост инерции почти всегда имеет перевес по вертикали, поэтому после каждой прокрутки карусели
 * страница чуть опускалась.
 *
 * Гасим только хвост: окно отсчитывается от последнего события с перевесом по горизонтали и
 * событиями-хвостами НЕ продлевается, а крупный вертикальный рывок пропускаем сразу. Так
 * намеренная прокрутка страницы сразу после карусели по-прежнему работает.
 */
function holdPageDuringWheelGesture(track, swiper) {
  let horizontalUntil = 0;

  track.addEventListener('wheel', (event) => {
    // Запертая лента жест не перехватывает (см. stopWhenAllCardsFit) — и гасить нечего:
    // страница должна вести себя как везде.
    if (!swiper.enabled) {
      return;
    }

    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
      horizontalUntil = performance.now() + WHEEL_TAIL_MS;

      return;
    }

    if (performance.now() < horizontalUntil && Math.abs(event.deltaY) < WHEEL_TAIL_MAX_DELTA) {
      event.preventDefault();
    }
  }, { passive: false });
}

/**
 * Ссылка «Read more» открывается в новой вкладке, указатель с карточки не уходит — вернувшись,
 * пользователь видел залипшую подсветку. Снять :hover из скрипта нельзя, поэтому гасим его
 * классом (селектор ховера в _cases.scss проверяет его отсутствие), а класс снимаем, когда
 * указатель ДЕЙСТВИТЕЛЬНО сдвинулся: иначе дрожание руки в момент клика вернуло бы подсветку
 * сразу же.
 */
function suppressHoverAfterClick(track) {
  const RELEASE_PX = 4;

  let card = null;
  let at = { x: 0, y: 0 };

  track.addEventListener('click', (event) => {
    const clicked = event.target.closest('.case-card');

    if (!clicked) {
      return;
    }

    if (card && card !== clicked) {
      card.classList.remove('case-card--hover-off');
    }

    card = clicked;
    at = { x: event.clientX, y: event.clientY };
    clicked.classList.add('case-card--hover-off');
  }, { passive: true });

  document.addEventListener('pointermove', (event) => {
    if (!card) {
      return;
    }

    if (Math.abs(event.clientX - at.x) < RELEASE_PX && Math.abs(event.clientY - at.y) < RELEASE_PX) {
      return;
    }

    card.classList.remove('case-card--hover-off');
    card = null;
  }, { passive: true });
}
